import { getTranslation, useIntlStore } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { SubscriptionBilling, dateFormatSkeleton } from '@easy-expense/utils-shared';
import React from 'react';

import { RowCard } from './RowCard.component';
import { Button } from '../../Button.components';

function capitalizeFirstLetter(val: string) {
  return val.charAt(0).toUpperCase() + val.slice(1);
}

const SectionHeader: React.FC<{ title: string; action?: React.ReactNode }> = ({
  title,
  action,
}) => (
  <Layout.Row align justify="space-between">
    <OpenSans.Primary weight="bold-700" size="m-18" color="primary">
      {title}
    </OpenSans.Primary>
    {action}
  </Layout.Row>
);

const KeyValueRow: React.FC<{ label: string; value: string; muted?: boolean }> = ({
  label,
  value,
  muted,
}) => (
  <Layout.Row py={6}>
    <OpenSans.Primary size="s-16" style={{ width: 140 }} color="secondary">
      {label}
    </OpenSans.Primary>
    <OpenSans.Primary size="s-16" color={muted ? 'secondary' : 'primary'}>
      {value}
    </OpenSans.Primary>
  </Layout.Row>
);

export const BillingTab: React.FC<{
  billingInfo: SubscriptionBilling;
  onUpdatePaymentMethod: () => void;
  onViewHistory: () => void;
}> = ({ billingInfo, onUpdatePaymentMethod, onViewHistory }) => {
  const { formatDate } = useIntlStore();
  const card = billingInfo.paymentMethod?.card;
  const cardLabel = `${capitalizeFirstLetter(card?.type ?? '')} ${getTranslation(
    'ending in',
  )} ${card?.last4 ?? 'xxxx'}`.trim();
  const expiryMonth =
    card?.expiryMonth && card.expiryYear
      ? formatDate(new Date(card.expiryYear, card.expiryMonth - 1, 1), {
          skeleton: dateFormatSkeleton.month,
        })
      : '';
  const expiry = `${getTranslation('Expires')} ${expiryMonth} ${card?.expiryYear ?? ''}`.trim();

  return (
    <Layout.Column>
      <SectionHeader title={getTranslation('Payment method')} />
      <Spacer.Vertical size={12} />
      <RowCard
        iconName="card-outline"
        title={cardLabel}
        description={`${expiry} · ${getTranslation('Default for subscription charges')}`}
        action={
          <Button.Secondary content={getTranslation('Update')} onClick={onUpdatePaymentMethod} />
        }
      />

      <Spacer.Vertical size={32} />

      <SectionHeader title={getTranslation('Billing details')} />
      <Spacer.Vertical size={12} />
      <Layout.Column bg="white" border={[1, 'solid', 'inputBorder']} radius={16} py={16} px={20}>
        <KeyValueRow
          label={getTranslation('Billing email')}
          value={billingInfo.customer?.email ?? ''}
        />
        <KeyValueRow
          label={getTranslation('Country')}
          value={billingInfo.address?.countryCode ?? ''}
        />
        <KeyValueRow
          label={getTranslation('Postal code')}
          value={billingInfo.address?.postalCode ?? ''}
        />
      </Layout.Column>

      <Spacer.Vertical size={32} />

      <SectionHeader title={getTranslation('Billing history')} />
      <Spacer.Vertical size={12} />
      <Layout.PressableRow
        align
        bg="white"
        border={[1, 'solid', 'inputBorder']}
        radius={16}
        py={16}
        px={20}
        onClick={onViewHistory}
        style={{ gap: 16 }}
      >
        <Layout.Column
          center
          radius={10}
          style={{
            width: 40,
            height: 40,
            backgroundColor: theme.colors.brandPrimaryXLight,
            flexShrink: 0,
          }}
        >
          <Icon name="receipt-outline" size={20} color={theme.colors.brandPrimary} />
        </Layout.Column>
        <Layout.Column grow>
          <OpenSans.Primary weight="bold-700" size="s-16" color="primary">
            {getTranslation('View invoices and receipts')}
          </OpenSans.Primary>
          <Spacer.Vertical size={2} />
          <OpenSans.Primary size="xs-12" color="secondary">
            {getTranslation('Download past invoices from the billing portal.')}
          </OpenSans.Primary>
        </Layout.Column>
        <Icon name="chevron-forward" size={20} color={theme.colors.brandPrimary} />
      </Layout.PressableRow>
    </Layout.Column>
  );
};
